import { useState } from 'react';
import { Box, Typography, Button, Snackbar, Alert } from '@mui/material';
import { Add, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import necropolisService from '../services/necropolis.service';
import { Necropolis } from '../types';
import { NecropolisTable, NecropolisToolbar } from '../components/necropolis/NecropolisTable';
import { NecropolisForm } from '../components/necropolis/NecropolisForm';

export default function NecropolisPage() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Necropolis | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const queryClient = useQueryClient();

  const { data: necropolisData, isLoading } = useQuery({
    queryKey: ['necropolis', { page, limit, search }],
    queryFn: () => necropolisService.getList({ page, limit, search }),
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<Necropolis, 'id' | 'createdAt' | 'updatedAt'>) => necropolisService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['necropolis'] });
      setIsFormOpen(false);
      setEditingItem(null);
      setNotification({ message: 'Запись добавлена', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при создании', type: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Necropolis> }) => necropolisService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['necropolis'] });
      setIsFormOpen(false);
      setEditingItem(null);
      setNotification({ message: 'Запись обновлена', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при обновлении', type: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => necropolisService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['necropolis'] });
      setNotification({ message: 'Запись удалена', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при удалении', type: 'error' }),
  });

  const filteredItems = necropolisData?.data || [];
  const total = necropolisData?.pagination?.total || 0;

  const handleCreate = () => { setEditingItem(null); setIsFormOpen(true); };
  const handleEdit = (item: Necropolis) => { setEditingItem(item); setIsFormOpen(true); };
  const handleSubmit = async (data: any) => {
    if (editingItem) await updateMutation.mutateAsync({ id: editingItem.id, data });
    else await createMutation.mutateAsync(data);
  };
  const handleDelete = (id: number) => { if (window.confirm('Удалить запись?')) deleteMutation.mutate(id); };
  const handleCloseNotification = () => setNotification(null);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontFamily: 'Noto Serif' }}>Некрополис</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>Добавить запись</Button>
      </Box>
      <NecropolisToolbar search={search} onSearchChange={(value) => { setSearch(value); setPage(1); }} />
      <NecropolisTable 
        items={filteredItems} 
        loading={isLoading} 
        total={total} 
        page={page} 
        limit={limit} 
        onPageChange={setPage} 
        onEdit={handleEdit} 
        onDelete={handleDelete} 
      />
      <NecropolisForm 
        open={isFormOpen} 
        onClose={() => setIsFormOpen(false)} 
        onSubmit={handleSubmit} 
        initialData={editingItem as any} 
        loading={createMutation.isPending || updateMutation.isPending} 
        error={createMutation.error?.message || updateMutation.error?.message} 
      />
      <Snackbar open={!!notification} autoHideDuration={3000} onClose={handleCloseNotification} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseNotification} severity={notification?.type === 'success' ? 'success' : 'error'} sx={{ width: '100%' }} icon={notification?.type === 'success' ? <CheckCircle /> : <ErrorIcon />}>{notification?.message}</Alert>
      </Snackbar>
    </Box>
  );
}